import { useStored } from "../travel/tripStore";
import { BackupBar } from "@/components/BackupBar";
import { pnum } from "../converters/numberInput";
import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { copyText } from "@/lib/copyText";

interface Item {
  id: number;
  name: string;
  price: string;
  qty: string;
  bought: boolean;
}

const TOOL_INFO_CONTENT = {
  what: `Tracks a general shopping list (distinct from a recurring grocery run) against a budget, with running totals for what's already been bought and what's left to spend.`,
  how: `Set a budget for the trip, add items with their price and quantity, check items off as you buy them, and your running totals update automatically.`,
  tip: `Add prices as estimates when you first create the list, then update them with actual prices as you shop — comparing the estimate against the real cost is a quick way to spot where a budget assumption was off.`,
};

export default function ShoppingListPlanner() {
  const [budget, setBudget] = useStored("fdp-ShoppingListPlanner-budget-v1", "200");
  const [items, setItems] = useStored<Item[]>("fdp-ShoppingListPlanner-items-v1", [
    { id: 1, name: "Winter jacket", price: "80", qty: "1", bought: false },
    { id: 2, name: "Birthday gift", price: "35", qty: "1", bought: false },
  ]);
  const [nextId, setNextId] = useStored("fdp-ShoppingListPlanner-nextId-v1", 3);
  const [newName, setNewName] = useState("");

  const addItem = () => {
    if (!newName.trim()) return;
    setItems([
      ...items,
      { id: nextId, name: newName.trim(), price: "0", qty: "1", bought: false },
    ]);
    setNextId(nextId + 1);
    setNewName("");
  };

  const update = (id: number, field: keyof Item, value: string | boolean) =>
    setItems(items.map(i => (i.id === id ? { ...i, [field]: value } : i)));
  const remove = (id: number) => setItems(items.filter(i => i.id !== id));

  const totals = useMemo(() => {
    const total = items.reduce(
      (sum, i) => sum + (pnum(i.price) || 0) * (pnum(i.qty) || 1),
      0
    );
    const spent = items
      .filter(i => i.bought)
      .reduce(
        (sum, i) => sum + (pnum(i.price) || 0) * (pnum(i.qty) || 1),
        0
      );
    const remaining = pnum(budget || "0") - total;
    return { total, spent, remaining };
  }, [items, budget]);

  const { logUsageNow } = useLogUsageOnAction();

  const copyList = async () => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    const text = items
      .map(i => `${i.bought ? "[x]" : "[ ]"} ${i.name} — $${i.price} x${i.qty}`)
      .join("\n");
    void copyText(text, "Shopping list copied");
  };


  return (
    <ToolContainer>
      <Label className="mb-2 block">Budget for this trip ($)</Label>
      <Input aria-label="Budget for this trip ($)"
        value={budget}
        onChange={e => setBudget(e.target.value)}
        className="max-w-xs mb-6"
      />

      <div className="flex gap-2 mb-4">
        <Input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Add an item..."
          onKeyDown={e => e.key === "Enter" && addItem()}
        />
        <Button onClick={addItem}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        {items.map(item => (
          <div
            key={item.id}
            className="grid grid-cols-[24px_1fr_90px_60px_32px] gap-2 items-center"
          >
            <input
              type="checkbox"
              checked={item.bought}
              onChange={e => update(item.id, "bought", e.target.checked)}
            />
            <span
              className={
                item.bought ? "line-through text-muted-foreground" : ""
              }
            >
              {item.name}
            </span>
            <Input
              value={item.price}
              onChange={e => update(item.id, "price", e.target.value)}
              placeholder="Price"
            />
            <Input
              value={item.qty}
              onChange={e => update(item.id, "qty", e.target.value)}
              placeholder="Qty"
            />
            <button
              onClick={() => remove(item.id)}
              className="text-muted-foreground hover:text-red-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6">
        <Card className="p-4 text-center">
          <p className="text-lg font-bold">${totals.total.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">List Total</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-lg font-bold text-green-600">
            ${totals.spent.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Already Bought</p>
        </Card>
        <Card className="p-4 text-center">
          <p
            className={`text-lg font-bold ${totals.remaining >= 0 ? "text-green-600" : "text-red-600"}`}
          >
            ${totals.remaining.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Budget Remaining</p>
        </Card>
      </div>

      <Button variant="outline" className="mt-4" onClick={copyList}>
        Copy List
      </Button>
          <BackupBar tool="shopping-list-planner" prefixes={["fdp-ShoppingListPlanner-"]} session />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
